/**
 * ============================================================================
 * VerifyEmailPage.tsx - Email Verification Holding Screen
 * ============================================================================
 * ENTERPRISE: Smooth referral signup flow
 * 
 * Flow:
 * 1. User signs up → redirected here
 * 2. Shows "Check your email" with resend option
 * 3. Auto-detects when email is verified (polls Firebase)
 * 4. Auto-redirects to onboarding once verified
 * 
 * Features:
 * - Auto-refresh detection (polls every 3 seconds)
 * - Resend email with cooldown (60 seconds)
 * - Clear instructions for users
 * - Mobile-friendly design
 * - Handles edge cases (already verified, not logged in)
 * ============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  Heading,
  Text,
  Button,
  useToast,
  Spinner,
  HStack,
  Circle,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { EmailIcon, CheckCircleIcon, RepeatIcon, ArrowForwardIcon } from '@chakra-ui/icons';
import { auth, db } from '../services/firebase';
import { sendEmailVerification, reload } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import gameApiService from '../services/gameApiService';
import { isEmulatorEnv } from '../utils/apiBase';

// ============================================================================
// ANIMATIONS
// ============================================================================
const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.8; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

// ============================================================================
// COMPONENT
// ============================================================================
const VerifyEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  
  const [isVerified, setIsVerified] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [checkCount, setCheckCount] = useState(0);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  // Admin-bypass: lets a user with isAdmin=true on their users doc skip the
  // email-verification gate so they can test with throwaway accounts.
  const [isAdmin, setIsAdmin] = useState(false);
  const [bypassing, setBypassing] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  // Shares the apiBase detection (env flag OR localhost auto-detect) so the
  // bootstrap button shows whenever API calls are actually going to the
  // emulator. Previously this only checked the env flag, which broke when
  // apiBase switched to hostname auto-detection.
  const isEmulator = isEmulatorEnv();

  // ============================================================================
  // CHECK VERIFICATION STATUS
  // ============================================================================
  const checkVerification = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) {
      // Not logged in - redirect to login
      navigate('/login');
      return;
    }

    setUserEmail(user.email);

    // Already verified - go to onboarding
    if (user.emailVerified) {
      setIsVerified(true);
      toast({
        title: 'Email Verified!',

        description: 'Redirecting to complete your profile...',
        status: 'success',
        duration: 2000,
      });
      setTimeout(() => navigate('/onboarding'), 1500);
      return;
    }

    // Reload user to check latest verification status
    setIsChecking(true);
    try {
      await reload(user);
      setCheckCount(prev => prev + 1);
      
      if (user.emailVerified) {
        setIsVerified(true);
        toast({
          title: 'Email Verified!',

          description: 'Redirecting to complete your profile...',
          status: 'success',
          duration: 2000,
        });
        setTimeout(() => navigate('/onboarding'), 1500);
      }
    } catch (error) {
      console.error('Error checking verification:', error);
    } finally {
      setIsChecking(false);
    }
  }, [navigate, toast]);

  // ============================================================================
  // AUTO-CHECK POLLING (every 3 seconds)
  // ============================================================================
  useEffect(() => {
    // Initial check
    checkVerification();

    // Poll every 3 seconds
    const interval = setInterval(() => {
      if (!isVerified) {
        checkVerification();
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [checkVerification, isVerified]);

  // ============================================================================
  // ADMIN DETECTION - check users/{uid} for isAdmin/role=admin on mount
  // ============================================================================
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (snap.exists()) {
          const d = snap.data();
          setIsAdmin(d.isAdmin === true || d.role === 'admin');
        }
      } catch {
        // ignore - if we can't read the doc, just hide the bypass button
      }
    })();
  }, []);

  // ============================================================================
  // EMULATOR BOOTSTRAP - promote self to admin + verified
  // Only visible/clickable when isEmulatorEnv() (env flag or localhost
  // auto-detect). The endpoint also refuses to run outside the emulator
  // regardless of client state.
  // ============================================================================
  const handleEmulatorBootstrap = async () => {
    const user = auth.currentUser;
    if (!user) return;
    setBootstrapping(true);
    const r = await gameApiService.emulatorBootstrap();
    if (!r.success) {
      setBootstrapping(false);
      toast({
        title: 'Bootstrap failed',
        description: r.error || 'Server rejected the request',
        status: 'error',
        duration: 4000,
      });
      return;
    }
    try {
      await user.getIdToken(true);
      await reload(user);
    } catch (err) {
      console.error('[EMULATOR_BOOTSTRAP] reload failed:', err);
    }
    setBootstrapping(false);
    toast({
      title: 'You are now admin',
      description: 'Reloading so the app picks up admin role',
      status: 'success',
      duration: 1500,
    });
    setIsAdmin(true);
    setIsVerified(true);
    // Hard reload so AuthContext re-fetches users/{uid} and Profile + nav
    // immediately reflect the admin flag. A React Router navigate alone
    // leaves the in-memory userProfile stale.
    setTimeout(() => { window.location.href = '/onboarding'; }, 800);
  };

  // ============================================================================
  // ADMIN BYPASS - flip emailVerified on the server, then reload + continue
  // ============================================================================
  const handleAdminBypass = async () => {
    const user = auth.currentUser;
    if (!user) return;
    setBypassing(true);
    const r = await gameApiService.adminVerifyUser();
    if (!r.success) {
      setBypassing(false);
      toast({
        title: 'Bypass failed',
        description: r.error || 'Server rejected the verify request',
        status: 'error',
        duration: 4000,
      });
      return;
    }
    // Force-refresh the Firebase Auth token so emailVerified flips client-side
    try {
      await user.getIdToken(true);
      await reload(user);
    } catch (err) {
      console.error('[ADMIN_BYPASS] reload failed:', err);
    }
    setBypassing(false);
    toast({
      title: r.alreadyVerified ? 'Already verified' : 'Verified',
      description: 'Continuing as admin',
      status: 'success',
      duration: 1500,
    });
    setIsVerified(true);
    setTimeout(() => navigate('/onboarding'), 800);
  };

  // ============================================================================
  // RESEND COOLDOWN TIMER
  // ============================================================================
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // ============================================================================
  // RESEND VERIFICATION EMAIL
  // ============================================================================
  const handleResendEmail = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      await sendEmailVerification(user);
      setResendCooldown(60); // 60 second cooldown
      toast({
        title: 'Email Sent!',

        description: 'Check your inbox (and spam folder)',
        status: 'success',
        duration: 4000,
      });
    } catch (error: any) {
      console.error('Error resending email:', error);
      
      if (error.code === 'auth/too-many-requests') {
        toast({
          title: 'Too Many Requests',
          description: 'Please wait a few minutes before trying again',
          status: 'warning',
          duration: 5000,
        });
        setResendCooldown(300); // 5 minute cooldown
      } else {
        toast({
          title: 'Error',
          description: 'Could not send email. Please try again.',
          status: 'error',
          duration: 4000,
        });
      }
    }
  };

  // ============================================================================
  // MANUAL CHECK BUTTON
  // ============================================================================
  const handleManualCheck = () => {
    checkVerification();
    toast({
      title: 'Checking...',
      description: 'Looking for verification',
      status: 'info',
      duration: 2000,
    });
  };

  // ============================================================================
  // SIGN OUT AND START OVER
  // ============================================================================
  const handleSignOut = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // ============================================================================
  // RENDER - VERIFIED STATE
  // ============================================================================
  if (isVerified) {
    return (
      <Box
        minH="100vh"
        bg="linear-gradient(135deg, #0D1B2A 0%, #1B2838 50%, #0D1B2A 100%)"
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={4}
      >
        <VStack spacing={6} textAlign="center">
          <Circle
            size="100px"
            bg="green.500"
            animation={`${pulse} 1s ease-in-out infinite`}
          >
            <CheckCircleIcon boxSize={12} color="white" />
          </Circle>
          <Heading color="white" size="lg">Email Verified!</Heading>
          <HStack color="cyan.400">
            <Spinner size="sm" />
            <Text>Redirecting to profile setup...</Text>
          </HStack>
        </VStack>
      </Box>
    );
  }

  // ============================================================================
  // RENDER - WAITING FOR VERIFICATION
  // ============================================================================
  return (
    <Box
      minH="100vh"
      bg="linear-gradient(135deg, #0D1B2A 0%, #1B2838 50%, #0D1B2A 100%)"
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={4}
    >
      <VStack
        spacing={8}
        maxW="450px"
        w="100%"
        bg="rgba(255,255,255,0.03)"
        borderRadius="2xl"
        border="1px solid rgba(0,217,217,0.2)"
        p={8}
        textAlign="center"
      >
        {/* Email Icon */}
        <Circle
          size="100px"
          bg="rgba(0,217,217,0.1)"
          border="2px solid"
          borderColor="cyan.500"
          animation={`${float} 3s ease-in-out infinite`}
        >
          <EmailIcon boxSize={12} color="cyan.400" />
        </Circle>

        {/* Title */}
        <VStack spacing={2}>
          <Heading color="white" size="lg">
            Check Your Email
          </Heading>
          <Text color="gray.400" fontSize="md">
            We sent a verification link to:
          </Text>
          <Text color="cyan.400" fontWeight="bold" fontSize="lg">
            {userEmail || 'your email'}
          </Text>
        </VStack>

        {/* Instructions */}
        <Box
          bg="rgba(0,217,217,0.05)"
          borderRadius="lg"
          p={4}
          w="100%"
        >
          <VStack spacing={3} align="start">
            <HStack>
              <Circle size="24px" bg="cyan.500" color="white" fontSize="sm" fontWeight="bold">1</Circle>
              <Text color="gray.300" fontSize="sm">Open the email from PokerSweepStars</Text>
            </HStack>
            <HStack>
              <Circle size="24px" bg="cyan.500" color="white" fontSize="sm" fontWeight="bold">2</Circle>
              <Text color="gray.300" fontSize="sm">Click the verification link</Text>
            </HStack>
            <HStack>
              <Circle size="24px" bg="cyan.500" color="white" fontSize="sm" fontWeight="bold">3</Circle>
              <Text color="gray.300" fontSize="sm">Return here - we'll detect it automatically!</Text>
            </HStack>
          </VStack>
        </Box>

        {/* Auto-checking indicator */}
        <HStack color="gray.500" fontSize="sm">
          {isChecking ? (
            <>
              <Spinner size="xs" color="cyan.400" />
              <Text>Checking verification status...</Text>
            </>
          ) : (
            <>
              <RepeatIcon />
              <Text>Auto-checking every 3 seconds (checked {checkCount}x)</Text>
            </>
          )}
        </HStack>

        {/* Action Buttons */}
        <VStack spacing={3} w="100%">
          {/* Resend Email Button */}
          <Button
            w="100%"
            size="lg"
            variant="outline"
            borderColor="cyan.500"
            color="cyan.400"
            _hover={{ bg: 'rgba(0,217,217,0.1)' }}
            leftIcon={<EmailIcon />}
            onClick={handleResendEmail}
            isDisabled={resendCooldown > 0}
          >
            {resendCooldown > 0 
              ? `Resend in ${resendCooldown}s` 
              : 'Resend Verification Email'
            }
          </Button>

          {/* Manual Check Button */}
          <Button
            w="100%"
            size="lg"
            bg="cyan.500"
            color="white"
            _hover={{ bg: 'cyan.600' }}
            rightIcon={<ArrowForwardIcon />}
            onClick={handleManualCheck}
            isLoading={isChecking}
          >
            I've Verified - Continue
          </Button>

          {/* Emulator bootstrap - visible only when running against local
              emulators. Grants admin + verified to the current user. The
              server refuses to honor this outside the emulator. */}
          {isEmulator && (
            <Button
              w="100%"
              size="md"
              bg="orange.600"
              color="white"
              _hover={{ bg: 'orange.500' }}
              onClick={handleEmulatorBootstrap}
              isLoading={bootstrapping}
              loadingText="Bootstrapping"
            >
               Emulator: make me admin + verify

            </Button>
          )}

          {/* Admin bypass - visible to users already flagged as admin */}
          {isAdmin && (
            <Button
              w="100%"
              size="md"
              bg="purple.700"
              color="white"
              _hover={{ bg: 'purple.600' }}
              onClick={handleAdminBypass}
              isLoading={bypassing}
              loadingText="Bypassing"
            >
              Admin: skip verification
            </Button>
          )}
        </VStack>

        {/* Help Text */}
        <VStack spacing={2}>
          <Text color="gray.500" fontSize="xs">
            Can't find the email? Check your spam/junk folder.
          </Text>
          <Button
            variant="link"
            color="gray.500"
            fontSize="xs"
            onClick={handleSignOut}
            _hover={{ color: 'red.400' }}
          >
            Wrong email? Sign out and try again
          </Button>
        </VStack>
      </VStack>
    </Box>
  );
};

export default VerifyEmailPage;